/**
 * JHM (JSR Hilega-Milega) math — shared (Round 104).
 *
 * Exact Pine logic: RSI(source, rsiLen) → then a VWMA and an EMA of that RSI.
 * Bullish when EMA ≥ VWMA. Used by the JHM chart oscillator AND as a voter in
 * the OverView trend score (so it flows into OverView / Bkground / RockBand).
 */
export interface JhmParams {
  rsiLength: number;
  source: 'close' | 'linregClose';
  vwmaLength: number;
  emaLength: number;
}
export const DEFAULT_JHM: JhmParams = { rsiLength: 9, source: 'close', vwmaLength: 21, emaLength: 3 };

export function resolveJhm(p?: Partial<JhmParams>): JhmParams {
  return {
    rsiLength: Math.max(1, Math.floor(Number(p?.rsiLength) || DEFAULT_JHM.rsiLength)),
    source: p?.source === 'linregClose' ? 'linregClose' : 'close',
    vwmaLength: Math.max(1, Math.floor(Number(p?.vwmaLength) || DEFAULT_JHM.vwmaLength)),
    emaLength: Math.max(1, Math.floor(Number(p?.emaLength) || DEFAULT_JHM.emaLength)),
  };
}

/** Wilder RMA (the smoothing Pine's rsi() uses), SMA-seeded. */
export function rmaWilder(v: number[], p: number): number[] {
  const out = new Array(v.length).fill(NaN);
  if (v.length < p || p < 1) return out;
  let sum = 0; for (let i = 0; i < p; i++) sum += v[i];
  let prev = sum / p; out[p - 1] = prev;
  for (let i = p; i < v.length; i++) { prev = (prev * (p - 1) + v[i]) / p; out[i] = prev; }
  return out;
}

/** Pine rsi(src, len): Wilder RSI of a source series. */
export function rsiWilder(src: number[], p: number): number[] {
  const n = src.length; const out = new Array(n).fill(NaN);
  if (n < p + 1) return out;
  const gain = new Array(n - 1).fill(0), loss = new Array(n - 1).fill(0);
  for (let i = 1; i < n; i++) {
    const d = src[i] - src[i - 1];
    gain[i - 1] = Number.isFinite(d) && d > 0 ? d : 0;
    loss[i - 1] = Number.isFinite(d) && d < 0 ? -d : 0;
  }
  const ag = rmaWilder(gain, p), al = rmaWilder(loss, p);
  for (let i = 0; i < ag.length; i++) {
    const g = ag[i], l = al[i];
    if (!Number.isFinite(g) || !Number.isFinite(l)) continue;
    out[i + 1] = l === 0 ? 100 : 100 - 100 / (1 + g / l);
  }
  return out;
}

/** Pine vwma(src, len): volume-weighted MA of a series (bar volume = weights). */
export function vwmaSeries(values: number[], vols: number[], p: number): number[] {
  const n = values.length; const out = new Array(n).fill(NaN);
  for (let i = p - 1; i < n; i++) {
    let pv = 0, vv = 0, ok = true;
    for (let j = i - p + 1; j <= i; j++) { const v = values[j]; if (!Number.isFinite(v)) { ok = false; break; } const vol = vols[j] || 0; pv += v * vol; vv += vol; }
    if (ok && vv > 0) out[i] = pv / vv;
  }
  return out;
}

/** Standard EMA (SMA-seeded), NaN-aware — Pine ema(src, len). */
export function emaSeries(v: number[], p: number): number[] {
  const n = v.length; const out = new Array(n).fill(NaN);
  const k = 2 / (p + 1); let prev = NaN, seeded = false, sum = 0, cnt = 0;
  for (let i = 0; i < n; i++) {
    const x = v[i]; if (!Number.isFinite(x)) continue;
    if (!seeded) { sum += x; cnt++; if (cnt === p) { prev = sum / p; out[i] = prev; seeded = true; } continue; }
    prev = x * k + prev * (1 - k); out[i] = prev;
  }
  return out;
}

/** Per-bar EMA & VWMA of the RSI. `srcArr` = the chosen source (close or linreg-close). */
export function jhmEmaVwma(srcArr: number[], vols: number[], params: JhmParams): { ema: number[]; vwma: number[] } {
  const rsi = rsiWilder(srcArr, params.rsiLength);
  return { ema: emaSeries(rsi, params.emaLength), vwma: vwmaSeries(rsi, vols, params.vwmaLength) };
}
